/**
 * ExportPanel Component
 * 
 * Panel untuk export settings:
 * - Lock aspect ratio
 * - Custom resolution
 * - Transparent background
 * - Video texture sync mode
 * - Screenshot & video recording controls
 */

import React from 'react'

export default function ExportPanel({
  isOpen,
  onClose,
  isMobile,
  viewportReady,
  // Resolution settings
  lockAspectRatio,
  setLockAspectRatio,
  useCustomResolution,
  setUseCustomResolution,
  transparentBackground,
  setTransparentBackground,
  exportWidth,
  setExportWidth,
  exportHeight,
  setExportHeight,
  // Video settings
  isVideoSettingsExpanded,
  setIsVideoSettingsExpanded,
  videoSyncMode,
  setVideoSyncMode,
  videoElementsRef,
  animationDuration,
  videoFrameRate = 30,
  // Export functions
  captureScreenshot,
  startRecording,
  stopRecording,
  isRecording,
  recordingProgress,
}) {
  if (!isOpen || !viewportReady) return null

  const hasVideoTextures = Object.keys(videoElementsRef?.current || {}).length > 0

  return (
    <div
      style={{
        position: 'absolute',
        top: '20px',
        left: '88px',
        zIndex: 18,
        width: isMobile ? 'calc(100% - 120px)' : '340px',
        background: 'linear-gradient(135deg, rgba(20, 20, 30, 0.72) 0%, rgba(10, 10, 15, 0.78) 100%)',
        backdropFilter: 'blur(24px) saturate(190%)',
        WebkitBackdropFilter: 'blur(24px) saturate(190%)',
        borderRadius: '16px',
        padding: '20px',
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15), 0 16px 48px rgba(0, 0, 0, 0.12)',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        border: '0.5px solid rgba(255, 255, 255, 0.18)',
        borderTop: '0.5px solid rgba(255, 255, 255, 0.25)',
        color: '#fff',
        animation: 'slideInFromLeft 0.5s cubic-bezier(0.32, 0.72, 0, 1) 0.1s forwards',
        opacity: 0,
        userSelect: 'none',
        WebkitUserSelect: 'none',
        MozUserSelect: 'none',
        msUserSelect: 'none',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '16px',
        }}
      >
        <h3
          style={{
            margin: 0,
            fontSize: '16px',
            fontWeight: 600,
          }}
        >
          📐 Export & Resolution
        </h3>
        <button
          onClick={onClose}
          style={{
            padding: '6px 10px',
            backgroundColor: 'rgba(239, 68, 68, 0.2)',
            color: '#ef4444',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '12px',
          }}
        >
          ✕
        </button>
      </div>

      {/* Lock Aspect Ratio */}
      <div style={{ marginBottom: '16px' }}>
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
          }}
        >
          <input
            type="checkbox"
            checked={lockAspectRatio}
            onChange={(e) => setLockAspectRatio(e.target.checked)}
            style={{
              marginRight: '10px',
              width: '18px',
              height: '18px',
              cursor: 'pointer',
              accentColor: '#3b82f6',
            }}
          />
          <span style={{ fontSize: '13px' }}>
            🔒 Lock Viewport Aspect Ratio
          </span>
        </label>
      </div>

      {/* Custom Resolution Toggle */}
      <div style={{ marginBottom: '16px' }}>
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
          }}
        >
          <input
            type="checkbox"
            checked={useCustomResolution}
            onChange={(e) => setUseCustomResolution(e.target.checked)}
            style={{
              marginRight: '10px',
              width: '18px',
              height: '18px',
              cursor: 'pointer',
              accentColor: '#3b82f6',
            }}
          />
          <span style={{ fontSize: '13px' }}>
            📏 Use Custom Resolution
          </span>
        </label>
      </div>

      {/* Transparent Background Toggle */}
      <div style={{ marginBottom: '16px' }}>
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
          }}
        >
          <input
            type="checkbox"
            checked={transparentBackground}
            onChange={(e) => setTransparentBackground(e.target.checked)}
            style={{
              marginRight: '10px',
              width: '18px',
              height: '18px',
              cursor: 'pointer',
              accentColor: '#3b82f6',
            }}
          />
          <span style={{ fontSize: '13px' }}>
            ✨ Transparent Background (PNG only)
          </span>
        </label>
        {transparentBackground && (
          <p
            style={{
              fontSize: '11px',
              opacity: 0.6,
              margin: '6px 0 0 28px',
              color: '#fbbf24',
            }}
          >
            ⚠ Video recording disabled (WebM doesn't support transparency)
          </p>
        )}
      </div>

      {/* Resolution Inputs */}
      {useCustomResolution && (
        <div
          style={{
            display: 'flex',
            gap: '12px',
            marginBottom: '16px',
          }}
        >
          <div style={{ flex: 1 }}>
            <label
              style={{
                fontSize: '11px',
                opacity: 0.6,
                display: 'block',
                marginBottom: '6px',
              }}
            >
              Width
            </label>
            <input
              type="number"
              value={exportWidth}
              onChange={(e) => setExportWidth(parseInt(e.target.value) || 1920)}
              style={{
                width: '100%',
                padding: '8px',
                backgroundColor: '#1e1e23',
                color: '#fff',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '6px',
                fontSize: '13px',
              }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label
              style={{
                fontSize: '11px',
                opacity: 0.6,
                display: 'block',
                marginBottom: '6px',
              }}
            >
              Height
            </label>
            <input
              type="number"
              value={exportHeight}
              onChange={(e) => setExportHeight(parseInt(e.target.value) || 1080)}
              style={{
                width: '100%',
                padding: '8px',
                backgroundColor: '#1e1e23',
                color: '#fff',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '6px',
                fontSize: '13px',
              }}
            />
          </div>
        </div>
      )}

      {/* Divider */}
      <div
        style={{
          height: '1px',
          background: 'rgba(255, 255, 255, 0.1)',
          margin: '20px 0',
        }}
      />

      {/* Video Settings Section - Collapsible */}
      <div style={{ marginBottom: '16px' }}>
        <div
          onClick={() => setIsVideoSettingsExpanded(!isVideoSettingsExpanded)}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            cursor: 'pointer',
            padding: '8px 0',
            marginBottom: '12px',
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          }}
        >
          <h4
            style={{
              margin: 0,
              fontSize: '14px',
              fontWeight: 600,
              color: '#10b981',
            }}
          >
            {isVideoSettingsExpanded ? '▼' : '▶'} 🎬 Video Settings
          </h4>
          {hasVideoTextures && (
            <span
              style={{
                fontSize: '10px',
                padding: '2px 8px',
                backgroundColor: 'rgba(59, 130, 246, 0.2)',
                color: '#60a5fa',
                borderRadius: '12px',
                fontWeight: '600',
              }}
            >
              {Object.keys(videoElementsRef.current).length} video{Object.keys(videoElementsRef.current).length > 1 ? 's' : ''}
            </span>
          )}
        </div>

        {isVideoSettingsExpanded && (
          <>
            {/* Video Texture Sync Mode */}
            <div style={{ marginBottom: '16px' }}>
              <label
                style={{
                  fontSize: '11px',
                  opacity: 0.6,
                  display: 'block',
                  marginBottom: '8px',
                }}
              >
                Video Texture Sync Mode
              </label>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={() => {
                    setVideoSyncMode('loop')
                    // Update existing videos
                    Object.values(videoElementsRef.current).forEach((videoData) => {
                      if (videoData) videoData.syncMode = 'loop'
                    })
                  }}
                  style={{
                    flex: 1,
                    padding: '10px',
                    backgroundColor: videoSyncMode === 'loop' ? '#3b82f6' : '#2d2d32',
                    color: videoSyncMode === 'loop' ? '#fff' : '#9ca3af',
                    border: videoSyncMode === 'loop' ? '1px solid #3b82f6' : '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: 600,
                    transition: 'all 0.2s',
                  }}
                >
                  🔄 Loop
                </button>
                <button
                  onClick={() => {
                    setVideoSyncMode('stretch')
                    // Update existing videos
                    Object.values(videoElementsRef.current).forEach((videoData) => {
                      if (videoData) videoData.syncMode = 'stretch'
                    })
                  }}
                  style={{
                    flex: 1,
                    padding: '10px',
                    backgroundColor: videoSyncMode === 'stretch' ? '#3b82f6' : '#2d2d32',
                    color: videoSyncMode === 'stretch' ? '#fff' : '#9ca3af',
                    border: videoSyncMode === 'stretch' ? '1px solid #3b82f6' : '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: 600,
                    transition: 'all 0.2s',
                  }}
                >
                  ⏱️ Stretch
                </button>
              </div>
              <p
                style={{
                  fontSize: '10px',
                  opacity: 0.5,
                  margin: '6px 0 0 0',
                  lineHeight: '1.4',
                }}
              >
                {videoSyncMode === 'loop'
                  ? '🔄 Video loops naturally (4s video = plays 2x in 8s animation)'
                  : '⏱️ Video stretches to match animation duration (4s video = slows to fit 8s animation)'}
              </p>

              {/* Video Texture Warning */}
              {hasVideoTextures && (
                <div
                  style={{
                    marginTop: '10px',
                    padding: '10px',
                    backgroundColor: 'rgba(251, 191, 36, 0.1)',
                    border: '1px solid rgba(251, 191, 36, 0.3)',
                    borderRadius: '6px',
                  }}
                >
                  <div
                    style={{
                      fontSize: '10px',
                      color: '#fbbf24',
                      fontWeight: '600',
                      marginBottom: '4px',
                    }}
                  >
                    ⚠️ {Object.keys(videoElementsRef.current).length} Video Texture(s) Detected
                  </div>
                  <div
                    style={{
                      fontSize: '9px',
                      opacity: 0.8,
                      lineHeight: '1.4',
                    }}
                  >
                    Rendering will be slower as each frame waits for video to seek. Expected time: ~{Math.ceil(videoFrameRate * animationDuration / 10)}s for {animationDuration.toFixed(1)}s animation
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Divider */}
      <div
        style={{
          height: '1px',
          background: 'rgba(255, 255, 255, 0.1)',
          margin: '20px 0',
        }}
      />

      {/* Export Buttons */}
      <div style={{ display: 'flex', gap: '8px' }}>
        <button
          onClick={captureScreenshot}
          disabled={isRecording}
          style={{
            flex: 1,
            padding: '12px',
            backgroundColor: isRecording ? 'rgba(59, 130, 246, 0.3)' : '#3b82f6',
            color: '#fff',
            border: 'none',
            borderRadius: '8px',
            cursor: isRecording ? 'not-allowed' : 'pointer',
            fontSize: '13px',
            fontWeight: 600,
            opacity: isRecording ? 0.5 : 1,
          }}
        >
          📸 Screenshot
        </button>
        <button
          onClick={isRecording ? stopRecording : startRecording}
          disabled={transparentBackground}
          style={{
            flex: 1,
            padding: '12px',
            backgroundColor: isRecording ? '#ef4444' : transparentBackground ? 'rgba(239, 68, 68, 0.3)' : '#10b981',
            color: '#fff',
            border: 'none',
            borderRadius: '8px',
            cursor: transparentBackground ? 'not-allowed' : 'pointer',
            fontSize: '13px',
            fontWeight: 600,
            opacity: transparentBackground ? 0.5 : 1,
          }}
        >
          {isRecording ? '⏹ Stop' : '🎬 Record'}
        </button>
      </div>

      {/* Recording Progress */}
      {isRecording && recordingProgress !== undefined && (
        <div
          style={{
            marginTop: '12px',
            padding: '10px',
            backgroundColor: 'rgba(16, 185, 129, 0.1)',
            borderRadius: '6px',
            border: '1px solid rgba(16, 185, 129, 0.3)',
          }}
        >
          <div
            style={{
              fontSize: '11px',
              marginBottom: '6px',
              fontWeight: 600,
              color: '#10b981',
            }}
          >
            Recording... {recordingProgress.toFixed(0)}%
          </div>
          <div
            style={{
              width: '100%',
              height: '4px',
              backgroundColor: 'rgba(0, 0, 0, 0.3)',
              borderRadius: '2px',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                width: `${recordingProgress}%`,
                height: '100%',
                backgroundColor: '#10b981',
                transition: 'width 0.3s ease',
              }}
            />
          </div>
        </div>
      )}
    </div>
  )
}

